import type { ComposerCapabilityCategory, ComposerCapabilityExample } from '@/types/composer';
import {
  capabilityCategoryHasReadyExamples,
  capabilityExampleIsReady,
} from '@/lib/capability-menu-stream-events';
import {
  getCapabilityDrawerExampleHintClassName,
  getCapabilityDrawerExampleLabelClassName,
} from '@/components/chat/chat-message-styles';
import { useIsMobile } from '@/hooks/use-mobile';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface ComposerCapabilityDrawerProps {
  category: ComposerCapabilityCategory;
  onExampleHover?: (example: ComposerCapabilityExample | null) => void;
  onExampleSelect: (example: ComposerCapabilityExample) => void;
  className?: string;
}

function ExampleSkeletonRow() {
  return (
    <li className="px-3 py-2">
      <Skeleton className="h-3.5 w-4/5 max-w-[14rem]" />
    </li>
  );
}

export function ComposerCapabilityDrawer({
  category,
  onExampleHover,
  onExampleSelect,
  className,
}: ComposerCapabilityDrawerProps) {
  const isMobile = useIsMobile();
  // Variable 2–4 finals: only skeleton while prefetching with nothing ready yet.
  // Do not pad toward a fixed length after seeds or stop reconciliation.
  const showSkeletons =
    category.streaming &&
    !capabilityCategoryHasReadyExamples(category) &&
    category.examples.length === 0;

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-[var(--radius-chat)] border border-border bg-popover text-popover-foreground shadow-lg',
        'max-h-[min(32vh,16rem)] overflow-y-auto',
        className
      )}
      role="dialog"
      aria-label={`${category.title} suggestions`}
    >
      <ul className="divide-y divide-border/60">
        {showSkeletons
          ? Array.from({ length: 2 }).map((_, index) => (
              <ExampleSkeletonRow key={`sk-${index}`} />
            ))
          : null}
        {category.examples.map((example, index) => {
          const isReady = capabilityExampleIsReady(example);
          return (
            <li key={`${category.id}-${index}`}>
              {isReady ? (
                <button
                  type="button"
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent hover:text-accent-foreground"
                  onMouseEnter={() => {
                    if (!isMobile) onExampleHover?.(example);
                  }}
                  onMouseLeave={() => {
                    if (!isMobile) onExampleHover?.(null);
                  }}
                  onClick={() => onExampleSelect(example)}
                >
                  <span className={getCapabilityDrawerExampleLabelClassName()}>{example.label}</span>
                  {isMobile && example.message ? (
                    <span className={getCapabilityDrawerExampleHintClassName()}>{example.message}</span>
                  ) : null}
                </button>
              ) : (
                <ExampleSkeletonRow />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
